import React from 'react';
import useScrollReveal from '@/hooks/useScrollReveal';

type RevealTag = 'div' | 'span' | 'section' | 'article' | 'li' | 'header' | 'h2' | 'h3';

export interface RevealProps extends React.HTMLAttributes<HTMLElement> {
    /** HTML tag to render. Defaults to `div`. */
    as?: RevealTag;
    /** Delay (ms) before the reveal transition starts, for staggered lists. */
    delay?: number;
    children: React.ReactNode;
}

/**
 * Fades and slides its children into view the first time they scroll into
 * the viewport. Automatically skips the animation (renders visible
 * immediately) when the user has `prefers-reduced-motion: reduce` set, or
 * when `IntersectionObserver` is unavailable.
 */
const Reveal: React.FC<RevealProps> = ({ as = 'div', delay = 0, className = '', style, children, ...rest }) => {
    const { ref, isVisible, prefersReducedMotion } = useScrollReveal<HTMLElement>();

    const mergedStyle: React.CSSProperties | undefined =
        !prefersReducedMotion && delay ? { ...style, transitionDelay: `${delay}ms` } : style;

    const classes = ['reveal', isVisible && 'reveal-visible', className].filter(Boolean).join(' ');

    return React.createElement(
        as,
        { ref, className: classes, style: mergedStyle, ...rest },
        children
    );
};

export default Reveal;
